import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import AppointmentReportsService from '../../services/appointmentReportsService';
import LocationService from '../../services/locationService';
import UserService from '../../services/userService';
import ReportControls from './ReportControls';
import './AppointmentReports.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AppointmentReports = () => {
  const auth = useAuth();
  const [reportType, setReportType] = useState('daily'); // 'daily' or 'monthly'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('all');

  // Handle window resize for responsive charts
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load locations and doctors for filtering
  useEffect(() => {
    const loadFilters = async () => {
      if (!auth.user?.access_token) return;
      try {
        const [locationsResult, doctorsResult] = await Promise.all([
          LocationService.getAllLocations(auth.user.access_token),
          UserService.getDoctors(auth.user.access_token),
        ]);
        setLocations(locationsResult.data || []);
        setDoctors(doctorsResult.data || []);
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    };
    loadFilters();
  }, [auth.user?.access_token]);

  // Helper function to format dates properly avoiding timezone issues
  const formatDateSafely = (dateString) => {
    // If the date is already in YYYY-MM-DD format without time, use it directly
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('es-ES');
    }
    
    // For full ISO strings, parse carefully to avoid timezone shifts
    if (typeof dateString === 'string' && dateString.includes('T')) {
      // Extract just the date part before the 'T'
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('es-ES');
    }
    
    // Fallback to regular date parsing
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  // Load report data
  const loadReportData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let data;
      if (reportType === 'daily') {
        data = await AppointmentReportsService.getDailyAppointmentReport(
          auth.user?.access_token,
          selectedYear,
          selectedMonth,
          selectedLocation,
          selectedDoctor
        );
      } else {
        data = await AppointmentReportsService.getMonthlyAppointmentReport(
          auth.user?.access_token,
          selectedYear,
          selectedLocation,
          selectedDoctor
        );
      }

      setReportData(data);
    } catch (error) {
      console.error('Error loading appointment report data:', error);
      setError('Error al cargar los datos del reporte de citas');
    } finally {
      setIsLoading(false);
    }
  }, [reportType, selectedYear, selectedMonth, selectedLocation, selectedDoctor, auth.user?.access_token]);

  useEffect(() => {
    if (auth.user?.access_token) {
      loadReportData();
    }
  }, [auth.user?.access_token, loadReportData]);

  // Prepare stacked bar chart data for daily view
  const getDailyStackedBarChartData = () => {
    if (!reportData || !reportData.dailyData) return null;

    return {
      labels: reportData.dailyData.map(day => `Día ${day.day}`),
      datasets: [
        {
          label: 'Completadas',
          data: reportData.dailyData.map(day => day.completed),
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 1,
        },
        {
          label: 'Programadas',
          data: reportData.dailyData.map(day => day.scheduled),
          backgroundColor: 'rgba(56, 182, 255, 0.8)',
          borderColor: 'rgba(56, 182, 255, 1)',
          borderWidth: 1,
        },
        {
          label: 'Confirmadas',
          data: reportData.dailyData.map(day => day.confirmed),
          backgroundColor: 'rgba(245, 158, 11, 0.8)',
          borderColor: 'rgba(245, 158, 11, 1)',
          borderWidth: 1,
        },
        {
          label: 'Canceladas',
          data: reportData.dailyData.map(day => day.cancelled),
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
          borderColor: 'rgba(239, 68, 68, 1)',
          borderWidth: 1,
        },
        {
          label: 'No Asistió',
          data: reportData.dailyData.map(day => day.noShow),
          backgroundColor: 'rgba(156, 163, 175, 0.8)',
          borderColor: 'rgba(156, 163, 175, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  // Prepare line chart data for monthly view
  const getMonthlyLineChartData = () => {
    if (!reportData || !reportData.monthlyData) return null;

    return {
      labels: reportData.monthlyData.map(month => month.monthName),
      datasets: [
        {
          label: 'Total de Citas',
          data: reportData.monthlyData.map(month => month.total),
          borderColor: 'rgba(79, 70, 229, 1)',
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgba(79, 70, 229, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          fill: true,
          tension: 0.3,
        },
        {
          label: 'Completadas',
          data: reportData.monthlyData.map(month => month.completed),
          borderColor: 'rgba(34, 197, 94, 1)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgba(34, 197, 94, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          fill: false,
          tension: 0.3,
        },
      ],
    };
  };

  const stackedBarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: isMobile ? 8 : 12,
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      title: {
        display: true,
        text: `Citas Diarias - ${reportData?.period?.monthName} ${reportData?.period?.year}`,
        font: {
          size: isMobile ? 14 : 16
        }
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: 'Días del Mes',
          font: {
            size: isMobile ? 10 : 12
          }
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Número de Citas',
          font: {
            size: isMobile ? 10 : 12
          }
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      }
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: isMobile ? 8 : 12,
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      title: {
        display: true,
        text: `Citas Mensuales - ${selectedYear}`,
        font: {
          size: isMobile ? 14 : 16
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Número de Citas',
          font: {
            size: isMobile ? 10 : 12
          }
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      }
    },
  };

  return (
    <div className="appointment-reports">
      <div className="reports-header">
        <h1>Reportes de Citas</h1>
        
        <ReportControls
          reportType={reportType}
          setReportType={setReportType}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          onRefresh={loadReportData}
          isLoading={isLoading}
          locations={locations}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          doctors={doctors}
          selectedDoctor={selectedDoctor}
          setSelectedDoctor={setSelectedDoctor}
        />
      </div>

      {/* Loading and Error States */}
      {isLoading && (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando reporte de citas...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      )}

      {/* Report Content */}
      {reportData && !isLoading && (
        <div className="reports-content">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <h3>Total de Citas</h3>
              <p className="count">{reportData.summary?.totalAppointments?.toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h3>Completadas</h3>
              <p className="count">{reportData.summary?.totalCompleted?.toLocaleString()}</p>
            </div>
            <div className="summary-card">
              <h3>Tasa de Finalización</h3>
              <p className="percentage completion">
                {((reportData.summary?.completionRate || 0) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="summary-card">
              <h3>Tasa de Cancelación</h3>
              <p className="percentage cancellation">
                {((reportData.summary?.cancellationRate || 0) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="summary-card">
              <h3>Promedio {reportType === 'daily' ? 'Diario' : 'Mensual'}</h3>
              <p className="avg-count">
                {reportType === 'daily' 
                  ? reportData.summary?.avgDailyAppointments?.toFixed(1)
                  : reportData.summary?.avgMonthlyAppointments?.toFixed(1)
                }
              </p>
            </div>
            <div className="summary-card">
              <h3>Canceladas + No Asistió</h3>
              <p className="count">
                {(reportData.summary?.totalCancelled + reportData.summary?.totalNoShow)?.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Chart */}
          <div className="chart-container">
            {reportType === 'daily' ? (
              getDailyStackedBarChartData() && (
                <Bar 
                  data={getDailyStackedBarChartData()} 
                  options={stackedBarChartOptions} 
                />
              )
            ) : (
              getMonthlyLineChartData() && (
                <Line 
                  data={getMonthlyLineChartData()} 
                  options={lineChartOptions} 
                />
              )
            )}
          </div>

          {/* Data Table */}
          <div className="data-table-container">
            <h3>Datos Detallados</h3>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    {reportType === 'daily' ? (
                      <>
                        <th>Día</th>
                        <th>Fecha</th>
                        <th>Total</th>
                        <th>Completadas</th>
                        <th>Programadas</th>
                        <th>Confirmadas</th>
                        <th>Canceladas</th>
                        <th>No Asistió</th>
                        <th>% Finalización</th>
                      </>
                    ) : (
                      <>
                        <th>Mes</th>
                        <th>Total</th>
                        <th>Completadas</th>
                        <th>Canceladas</th>
                        <th>No Asistió</th>
                        <th>% Finalización</th>
                        <th>% Cancelación</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {reportType === 'daily' 
                    ? reportData.dailyData?.map(day => (
                        <tr key={day.day}>
                          <td>{day.day}</td>
                          <td>{formatDateSafely(day.date)}</td>
                          <td>{day.total}</td>
                          <td>
                            <span className="status-indicator status-completed">
                              {day.completed}
                            </span>
                          </td>
                          <td>
                            <span className="status-indicator status-scheduled">
                              {day.scheduled}
                            </span>
                          </td>
                          <td>
                            <span className="status-indicator status-confirmed">
                              {day.confirmed}
                            </span>
                          </td>
                          <td>
                            <span className="status-indicator status-cancelled">
                              {day.cancelled}
                            </span>
                          </td>
                          <td>{day.noShow}</td>
                          <td>{(day.completionRate * 100).toFixed(1)}%</td>
                        </tr>
                      ))
                    : reportData.monthlyData?.map(month => (
                        <tr key={month.month}>
                          <td>{month.monthName}</td>
                          <td>{month.total}</td>
                          <td>
                            <span className="status-indicator status-completed">
                              {month.completed}
                            </span>
                          </td>
                          <td>
                            <span className="status-indicator status-cancelled">
                              {month.cancelled}
                            </span>
                          </td>
                          <td>{month.noShow}</td>
                          <td>{(month.completionRate * 100).toFixed(1)}%</td>
                          <td>{(month.cancellationRate * 100).toFixed(1)}%</td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentReports;
